/**
 * Week utilities — academic year calendar (Sunday start)
 *
 * Academic year weeks:
 * - Start on Sunday
 * - Week 1 is the week containing June 2 (academic year start)
 * - `year` in WeekInfo = academic year (e.g. 2025 = year starting June 2 2025)
 *
 * To change the academic year start date, update ACADEMIC_YEAR_START.
 */

import { UserRole } from '@/types/enums/UserRole';

// June 2 — change month/day here to shift the academic year start
const ACADEMIC_YEAR_START = { month: 5, day: 2 }; // month is 0-indexed (5 = June)

export interface WeekInfo {
  week: number; // 1-53
  year: number; // Academic year (e.g., 2025 = starts June 2 2025)
  startDate: Date; // Sunday of the week
  endDate: Date; // Saturday of the week
}

/** Sunday on or before the academic year start date for the given academic year */
function getAcademicYearAnchorSunday(academicYear: number): Date {
  const startDate = new Date(
    academicYear,
    ACADEMIC_YEAR_START.month,
    ACADEMIC_YEAR_START.day
  );
  const dayOfWeek = startDate.getDay();
  if (dayOfWeek !== 0) {
    startDate.setDate(startDate.getDate() - dayOfWeek);
  }
  return startDate;
}

/** Academic year a date belongs to — dates before the anchor Sunday roll back to previous year */
function getAcademicYear(date: Date): number {
  const year = date.getFullYear();
  const anchor = getAcademicYearAnchorSunday(year);
  return date >= anchor ? year : year - 1;
}

/**
 * Get academic week number for a given date.
 * Week 1 starts on the Sunday on/before June 2.
 */
export function getUSWeek(date: Date): WeekInfo {
  const d = new Date(date);
  const academicYear = getAcademicYear(d);
  const anchorSunday = getAcademicYearAnchorSunday(academicYear);

  const daysSinceAnchor = Math.floor(
    (d.getTime() - anchorSunday.getTime()) / (24 * 60 * 60 * 1000)
  );
  const weekNumber = Math.floor(daysSinceAnchor / 7) + 1;

  const currentDay = d.getDay();
  const startDate = new Date(d);
  startDate.setDate(d.getDate() - currentDay);

  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  return { week: weekNumber, year: academicYear, startDate, endDate };
}

/**
 * Get WeekInfo for a specific week number and academic year.
 */
export function getWeekInfo(week: number, academicYear: number): WeekInfo {
  const anchorSunday = getAcademicYearAnchorSunday(academicYear);

  const startDate = new Date(anchorSunday);
  startDate.setDate(anchorSunday.getDate() + (week - 1) * 7);

  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  return { week, year: academicYear, startDate, endDate };
}

/**
 * Check if a date falls within a specific week
 */
export function isDateInWeek(
  date: Date | string,
  week: number,
  year: number
): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;
  const weekInfo = getUSWeek(d);
  return weekInfo.week === week && weekInfo.year === year;
}

/**
 * Get current week info
 */
export function getCurrentWeek(): WeekInfo {
  return getUSWeek(new Date());
}

/**
 * Get previous week
 */
export function getPreviousWeek(current: WeekInfo): WeekInfo {
  if (current.week === 1) {
    // Last day of previous academic year = day before this year's anchor Sunday
    const prevYearLastDay = new Date(getAcademicYearAnchorSunday(current.year));
    prevYearLastDay.setDate(prevYearLastDay.getDate() - 1);
    return getUSWeek(prevYearLastDay);
  }
  return getWeekInfo(current.week - 1, current.year);
}

/**
 * Get next week
 */
export function getNextWeek(current: WeekInfo): WeekInfo {
  // Last day of this academic year = day before next year's anchor Sunday
  const nextYearAnchor = getAcademicYearAnchorSunday(current.year + 1);
  const thisYearLastDay = new Date(nextYearAnchor);
  thisYearLastDay.setDate(nextYearAnchor.getDate() - 1);
  const lastWeekOfYear = getUSWeek(thisYearLastDay);

  if (current.week === lastWeekOfYear.week) {
    return getWeekInfo(1, current.year + 1);
  }
  return getWeekInfo(current.week + 1, current.year);
}

/**
 * Format week for display
 * Examples: "Week 6, 2025", "Feb 10-16, 2025", "Dec 29, 2024 - Jan 4, 2025"
 */
export function formatWeek(
  weekInfo: WeekInfo,
  format: 'short' | 'long' = 'short'
): string {
  if (format === 'short') {
    return `Week ${weekInfo.week}, ${weekInfo.year}`;
  }

  // Guard against deserialized WeekInfo where dates are strings (e.g. from JSON.parse)
  const startDate =
    weekInfo.startDate instanceof Date
      ? weekInfo.startDate
      : new Date(weekInfo.startDate);
  const endDate =
    weekInfo.endDate instanceof Date
      ? weekInfo.endDate
      : new Date(weekInfo.endDate);

  const startMonth = startDate.toLocaleDateString('en-US', {
    month: 'short',
  });
  const startDay = startDate.getDate();
  const startYear = startDate.getFullYear();

  const endMonth = endDate.toLocaleDateString('en-US', {
    month: 'short',
  });
  const endDay = endDate.getDate();
  const endYear = endDate.getFullYear();

  // Handle year boundary case (week spans two years)
  if (startYear !== endYear) {
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }

  // Same month, same year
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}, ${weekInfo.year}`;
  }

  // Different months, same year
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${weekInfo.year}`;
}

/**
 * Check if two weeks are the same
 */
export function isSameWeek(a: WeekInfo, b: WeekInfo): boolean {
  return a.week === b.week && a.year === b.year;
}

/**
 * Check if a date falls in a past week (before the current week's Sunday start)
 */
export function isDateInPastWeek(date: Date | string): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const weekday = now.getDay(); // Sunday=0
  const sundayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - weekday,
    0,
    0,
    0,
    0
  );
  const cardDate = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    0,
    0,
    0,
    0
  );
  return cardDate < sundayStart;
}

/**
 * Check if a card is frozen based on its start date and user role.
 * Admins are never frozen.
 */
export function isCardFrozen(
  cardStartDate: Date | string | undefined,
  userRole: string | undefined
): boolean {
  if (!cardStartDate) return false;
  if (userRole === UserRole.ADMIN) return false;
  return isDateInPastWeek(cardStartDate);
}

/**
 * Check if a selected week is frozen for the given user role.
 * A week is frozen if it's entirely in the past (its end date is before current week's Sunday).
 * Admins are never frozen.
 */
export function isWeekFrozen(
  week: WeekInfo | undefined,
  userRole: string | undefined
): boolean {
  if (!week) return false;
  if (userRole === UserRole.ADMIN) return false;
  return isDateInPastWeek(week.endDate);
}

/**
 * Get array of date strings (YYYY-MM-DD) for all days in a week
 * Returns 7 dates from Sunday to Saturday
 */
export function getWeekDates(weekInfo: WeekInfo): string[] {
  const dates: string[] = [];
  const current = new Date(weekInfo.startDate);

  // Generate 7 dates (Sunday through Saturday)
  for (let i = 0; i < 7; i++) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);

    // Move to next day
    current.setDate(current.getDate() + 1);
  }

  return dates;
}
